import { Edit, Save } from "@mui/icons-material";
import { Box, IconButton, InputAdornment, TextField, Typography } from "@mui/material";
import { useState } from "react";

interface EditableEstimatedValueProps {
  value?: number;
  onUpdate: (value: number | null) => Promise<void>;
}

export const EditableEstimatedValue = ({ value, onUpdate }: EditableEstimatedValueProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [tempValue, setTempValue] = useState<string>(value?.toString() ?? "");
  const [isUpdating, setIsUpdating] = useState(false);

  const handleSave = async () => {
    if (isUpdating) {
      return;
    }

    setIsUpdating(true);
    try {
      const numericValue = tempValue ? parseFloat(tempValue) : null;
      await onUpdate(numericValue);
      setIsEditing(false);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    // Only allow numbers and decimal point
    const value = event.target.value.replace(/[^\d.]/g, "");
    // Ensure only one decimal point
    const parts = value.split(".");
    if (parts.length > 2) {
      return;
    }
    setTempValue(value);
  };

  const formatValue = (val?: number) => {
    if (val == null) {
      return "Not set";
    }
    return new Intl.NumberFormat("en-US", {
      currency: "USD",
      maximumFractionDigits: 2,
      minimumFractionDigits: 0,
      style: "currency",
    }).format(val);
  };

  return (
    <Box display="flex" alignItems="center" gap={1}>
      {isEditing ? (
        <>
          <TextField
            value={tempValue}
            onChange={handleInputChange}
            size="small"
            disabled={isUpdating}
            onKeyPress={(e) => {
              if (e.key === "Enter") {
                handleSave();
              }
            }}
            slotProps={{
              input: {
                startAdornment: <InputAdornment position="start">$</InputAdornment>,
              },
            }}
          />
          <IconButton onClick={handleSave} size="small" disabled={isUpdating} aria-label="Save estimated value">
            <Save />
          </IconButton>
        </>
      ) : (
        <>
          <Typography variant="h6">{formatValue(value)}</Typography>
          <IconButton onClick={() => setIsEditing(true)} size="small" aria-label="Edit estimated value">
            <Edit />
          </IconButton>
        </>
      )}
    </Box>
  );
};
